/**
 * Metadata available to traffic hook projectors and callbacks.
 */
export interface ITypedRequestLogMetadata {
  readonly correlationId: string;
  readonly method: string;
  readonly direction: 'outgoing' | 'incoming';
  readonly phase: 'request' | 'response';
  readonly timestamp: number;
  readonly durationMs?: number;
  readonly hasError: boolean;
}

/**
 * A projected, recipient-specific TypedRequest traffic entry.
 *
 * The mutable shape preserves the pre-3.8 source contract. Entries emitted by
 * TypedRequest are still frozen at runtime.
 */
export interface ITypedRequestLogEntry {
  correlationId: string;
  method: string;
  direction: 'outgoing' | 'incoming';
  phase: 'request' | 'response';
  timestamp: number;
  durationMs?: number;
  payload: any;
  error?: string;
  payloadRedacted?: boolean;
  hasError?: boolean;
}

export type TTypedRouterHookCallback = (
  entry: ITypedRequestLogEntry
) => void;

/**
 * Hooks for observing TypedRequest traffic.
 */
export interface ITypedRouterHooks {
  onOutgoingRequest?: TTypedRouterHookCallback;
  onIncomingResponse?: TTypedRouterHookCallback;
  onIncomingRequest?: TTypedRouterHookCallback;
  onOutgoingResponse?: TTypedRouterHookCallback;
}

/**
 * Explicit projections for a trusted hook registration.
 */
export interface ITypedRouterHookProjectionOptions {
  payloadProjector?: (
    payload: unknown,
    metadata: Readonly<ITypedRequestLogMetadata>
  ) => unknown;
  errorProjector?: (
    errorText: string,
    metadata: Readonly<ITypedRequestLogMetadata>
  ) => string | undefined;
}

/**
 * Default payload value for metadata-only hook entries.
 * Consumers must use payloadRedacted as the authoritative redaction signal.
 */
export const TYPED_REQUEST_REDACTED_PAYLOAD = Object.freeze({
  redacted: true,
} as const);
